import React, { useRef, useState } from "react";
import { UploadCloud, FileSpreadsheet, CheckCircle2, AlertCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/AppLayout";
import { toast } from "sonner";

const ACCEPT = ".xlsx,.xls,.ods";

export default function Upload() {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleSelect = (f) => {
    if (!f) return;
    const ok = /\.(xlsx|xls|ods)$/i.test(f.name);
    if (!ok) {
      setError("Only .xlsx, .xls or .ods files are supported");
      return;
    }
    setError("");
    setResult(null);
    setFile(f);
  };

  const onUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError("");
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Don't set Content-Type — axios + the browser will set
      // 'multipart/form-data; boundary=…' automatically.
      const { data } = await api.post("/questions/upload", fd);
      setResult(data);
      toast.success(`Imported ${data.inserted} questions`);
      setFile(null);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Upload Question Bank"
        subtitle="Import raw NCLEX-style questions from .xlsx, .xls or .ods spreadsheets."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div
            data-testid="upload-dropzone"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              handleSelect(e.dataTransfer.files?.[0]);
            }}
            className={`border-2 border-dashed rounded-2xl p-12 flex flex-col items-center justify-center gap-3 text-center cursor-pointer transition-all ${
              dragOver
                ? "border-orange-500 bg-orange-50/40"
                : "border-slate-300 bg-white hover:border-orange-400 hover:bg-orange-50/20"
            }`}
          >
            <div className="w-14 h-14 rounded-2xl bg-orange-50 text-orange-600 flex items-center justify-center">
              <UploadCloud className="w-7 h-7" />
            </div>
            <div className="text-base font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>
              Drop your spreadsheet here
            </div>
            <div className="text-sm text-slate-500">
              or <span className="text-orange-600 font-semibold">browse</span> from your computer
            </div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mt-2">
              .xlsx · .xls · .ods · up to ~50MB
            </div>
            <input
              ref={inputRef}
              data-testid="upload-file-input"
              type="file"
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => handleSelect(e.target.files?.[0])}
            />
          </div>

          {file && (
            <div className="mt-5 bg-white rounded-xl p-4 border border-slate-100 brand-shadow flex items-center gap-4">
              <div className="w-11 h-11 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center">
                <FileSpreadsheet className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 truncate">{file.name}</div>
                <div className="text-xs text-slate-500 font-mono">
                  {(file.size / 1024).toFixed(1)} KB
                </div>
              </div>
              <button
                onClick={() => setFile(null)}
                className="text-sm text-slate-400 hover:text-slate-700"
              >
                Clear
              </button>
              <button
                data-testid="upload-submit-btn"
                onClick={onUpload}
                disabled={uploading}
                className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:bg-slate-200 disabled:text-slate-400 text-white text-sm font-semibold transition"
              >
                {uploading ? "Importing…" : "Import Questions"}
              </button>
            </div>
          )}

          {result && (
            <div className="mt-5 rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3 flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              <div className="text-sm text-emerald-800">
                Successfully imported <strong>{result.inserted}</strong> questions from{" "}
                <span className="font-mono">{result.filename}</span>.
              </div>
            </div>
          )}
          {error && (
            <div className="mt-5 rounded-xl bg-red-50 border border-red-100 px-4 py-3 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <div className="text-sm text-red-800">{error}</div>
            </div>
          )}
        </div>

        <aside className="bg-white rounded-2xl border border-slate-100 brand-shadow p-6">
          <h3 className="font-bold text-slate-900 mb-3" style={{ fontFamily: "Outfit" }}>
            Expected Columns
          </h3>
          <p className="text-xs text-slate-500 mb-4">
            We auto-map common headers. Best results when your sheet contains:
          </p>
          <ul className="space-y-1.5 text-xs font-mono text-slate-600">
            {[
              "ID",
              "Question Text",
              "Option A · B · C · D",
              "Correct Answer",
              "Rationale",
              "Exam",
              "Subject",
              "Category",
              "Bloom Level",
              "Question Type",
              "Difficulty",
              "Time",
              "Mark",
            ].map((c) => (
              <li key={c} className="flex items-center gap-2">
                <span className="w-1 h-1 rounded-full bg-orange-500" />
                {c}
              </li>
            ))}
          </ul>
          <div className="mt-5 pt-5 border-t border-slate-100 text-xs text-slate-500">
            Reviewer fields (rating, tags, notes, reviewer, date) are filled in-app during review.
          </div>
        </aside>
      </div>
    </PageContainer>
  );
}
